import React, { useEffect, useMemo, useRef, useState } from "react";
import { open_api_with_cookies } from "../../../Utils/network";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import "./AIPlayground.css";

const modelOptions = ["claude-sonnet-4-6", "claude-opus-4-6"];

const initialMessages = [
  {
    role: "user",
    content: "My toddler refuses every meal. I am exhausted.",
  },
];

const getChangedRange = (before, after) => {
  if (before === after) return { start: 0, end: 0 };

  let start = 0;
  while (
    start < before.length &&
    start < after.length &&
    before[start] === after[start]
  ) {
    start += 1;
  }

  let beforeEnd = before.length - 1;
  let afterEnd = after.length - 1;
  while (
    beforeEnd >= start &&
    afterEnd >= start &&
    before[beforeEnd] === after[afterEnd]
  ) {
    beforeEnd -= 1;
    afterEnd -= 1;
  }

  return {
    start,
    end: Math.max(start + 1, afterEnd + 1),
  };
};

const AIPlayground = () => {
  const [config, setConfig] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    version: "",
    default_model: "claude-sonnet-4-6",
    system_prompt: "",
    temperature: 0.7,
    max_tokens: 8000,
    cache_expiry_hours: 24,
    release_notes: "",
  });
  const [messages, setMessages] = useState(initialMessages);
  const [testInput, setTestInput] = useState("");
  const [patchRequest, setPatchRequest] = useState("");
  const [patchMode, setPatchMode] = useState("auto");
  const [patchSuggestion, setPatchSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [isPatching, setIsPatching] = useState(false);
  const [isApplyingPatch, setIsApplyingPatch] = useState(false);
  const [didApplyPatch, setDidApplyPatch] = useState(false);
  const [appliedRange, setAppliedRange] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const messagesRef = useRef(null);
  const promptTextareaRef = useRef(null);
  const applyPatchTimerRef = useRef(null);

  const promptStats = useMemo(() => {
    const text = form.system_prompt || "";
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    return {
      characters: text.length,
      words,
    };
  }, [form.system_prompt]);

  useEffect(() => {
    const fetchCurrentConfig = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await open_api_with_cookies.get(
          "/myburgoai/admin/config/current/"
        );
        hydrateForm(res.data);
        setConfig(res.data);
      } catch (err) {
        console.error("Error fetching AI config:", err);
        setError("Failed to load the current AI configuration.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentConfig();
  }, []);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isTesting]);

  useEffect(() => {
    return () => {
      if (applyPatchTimerRef.current) {
        clearTimeout(applyPatchTimerRef.current);
      }
    };
  }, []);

  const hydrateForm = (data) => {
    setForm({
      name: data.name || "MyBurgo Universal AI",
      version: data.version || "",
      default_model: data.default_model || "claude-sonnet-4-6",
      system_prompt: data.system_prompt || "",
      temperature: data.temperature ?? 0.7,
      max_tokens: data.max_tokens ?? 8000,
      cache_expiry_hours: data.cache_expiry_hours ?? 24,
      release_notes: data.release_notes || "",
    });
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    name: form.name,
    version: form.version,
    default_model: form.default_model,
    system_prompt: form.system_prompt,
    temperature: Number(form.temperature),
    max_tokens: Number(form.max_tokens),
    cache_expiry_hours: Number(form.cache_expiry_hours),
    release_notes: form.release_notes,
  });

  const showNotice = (message) => {
    setNotice(message);
    setError(null);
  };

  const resetToCurrent = () => {
    if (!config) return;
    hydrateForm(config);
    setDraftId(null);
    setMessages(initialMessages);
    setTestInput("");
    showNotice("Reset to the current live prompt.");
  };

  const saveDraft = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      delete payload.version;
      if (draftId) payload.id = draftId;

      const res = await open_api_with_cookies.post(
        "/myburgoai/admin/config/draft/",
        payload
      );
      if (res.status >= 400) {
        throw new Error(res.data?.detail || "Unable to save draft.");
      }
      setDraftId(res.data.id);
      showNotice("Draft saved. The live AI prompt has not changed.");
    } catch (err) {
      console.error("Error saving AI draft:", err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.system_prompt?.[0] ||
          "Unable to save draft."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const publishVersion = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (!payload.version || payload.version === config?.version) {
        delete payload.version;
      }
      if (draftId) payload.draft_id = draftId;

      const res = await open_api_with_cookies.post(
        "/myburgoai/admin/config/publish/",
        payload
      );
      if (res.status >= 400) {
        throw new Error(res.data?.detail || "Unable to publish version.");
      }
      setConfig(res.data);
      hydrateForm(res.data);
      setDraftId(null);
      showNotice("Published. This prompt is now the live AI configuration.");
    } catch (err) {
      console.error("Error publishing AI config:", err);
      setError(
        err.response?.data?.non_field_errors?.[0] ||
          err.response?.data?.version?.[0] ||
          "Unable to publish version."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const sendTestMessage = async () => {
    const content = testInput.trim();
    if (!content || isTesting) return;

    const nextMessages = [...messages, { role: "user", content }];
    setMessages(nextMessages);
    setTestInput("");
    setIsTesting(true);
    setError(null);

    try {
      const res = await open_api_with_cookies.post(
        "/myburgoai/admin/config/test/",
        {
          system_prompt: form.system_prompt,
          messages: nextMessages,
          model: form.default_model,
          temperature: Number(form.temperature),
          max_tokens: Number(form.max_tokens),
        }
      );

      if (res.status >= 400) {
        throw new Error(res.data?.error || "Unable to run sandbox test.");
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: res.data.content || "No response returned.",
          meta: {
            model: res.data.model,
            tokens:
              (res.data.input_tokens || 0) + (res.data.output_tokens || 0),
            responseTime: res.data.response_time_ms,
          },
        },
      ]);
    } catch (err) {
      console.error("Error testing AI prompt:", err);
      setError(err.response?.data?.error || "Unable to run sandbox test.");
      setMessages(nextMessages);
    } finally {
      setIsTesting(false);
    }
  };

  const generatePromptPatch = async () => {
    const changeRequest = patchRequest.trim();
    if (!changeRequest || isPatching) return;

    setIsPatching(true);
    setError(null);
    setPatchSuggestion(null);

    try {
      const res = await open_api_with_cookies.post(
        "/myburgoai/admin/config/patch-prompt/",
        {
          system_prompt: form.system_prompt,
          change_request: changeRequest,
          mode: patchMode,
          model: form.default_model,
        }
      );

      if (res.status >= 400) {
        throw new Error(res.data?.error || "Unable to generate prompt update.");
      }

      setPatchSuggestion(res.data);
      showNotice("Focused prompt update is ready to review.");
    } catch (err) {
      console.error("Error generating prompt update:", err);
      setError(
        err.response?.data?.error || "Unable to generate prompt update."
      );
    } finally {
      setIsPatching(false);
    }
  };

  const applyPromptPatch = () => {
    if (!patchSuggestion?.updated_prompt || isApplyingPatch) return;

    const range = getChangedRange(
      form.system_prompt,
      patchSuggestion.updated_prompt
    );

    setIsApplyingPatch(true);
    setDidApplyPatch(false);
    setAppliedRange(null);

    applyPatchTimerRef.current = setTimeout(() => {
      handleChange("system_prompt", patchSuggestion.updated_prompt);
      if (!form.release_notes && patchSuggestion.summary) {
        handleChange("release_notes", patchSuggestion.summary);
      }
      setPatchSuggestion(null);
      setPatchRequest("");
      setIsApplyingPatch(false);
      setDidApplyPatch(true);
      setAppliedRange(range);
      showNotice(
        "Focused update applied to the draft editor. Save or publish when ready."
      );

      setTimeout(() => {
        const textarea = promptTextareaRef.current;
        if (!textarea) return;

        const textBeforeChange = patchSuggestion.updated_prompt.slice(
          0,
          range.start
        );
        const linesBeforeChange = textBeforeChange.split("\n").length - 1;
        const lineHeight = parseFloat(
          window.getComputedStyle(textarea).lineHeight
        );
        textarea.focus();
        textarea.setSelectionRange(range.start, range.end);
        textarea.scrollTop = Math.max(
          0,
          linesBeforeChange * lineHeight - textarea.clientHeight / 3
        );
      }, 80);

      applyPatchTimerRef.current = setTimeout(() => {
        setDidApplyPatch(false);
        setAppliedRange(null);
      }, 900);
    }, 650);
  };

  const clearChat = () => {
    setMessages(initialMessages);
    setTestInput("");
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendTestMessage();
    }
  };

  if (isLoading) return <LoadingOverlay />;

  return (
    <div className="ai-playground">
      <div className="ai-playground-header">
        <div>
          <h2>AI Prompt Playground</h2>
          <span>
            Edit, test, save drafts, and publish the MyBurgo system prompt.
          </span>
        </div>
        <div className="ai-playground-actions">
          <button onClick={resetToCurrent} disabled={isSaving}>
            Reset Current
          </button>
          <button onClick={saveDraft} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Draft"}
          </button>
          <button
            className="publish"
            onClick={publishVersion}
            disabled={isSaving}
          >
            Publish Version
          </button>
        </div>
      </div>

      {(notice || error) && (
        <div className={`ai-playground-alert ${error ? "error" : "success"}`}>
          {error || notice}
        </div>
      )}

      <div className="ai-playground-summary">
        <div className="ai-playground-summary-card">
          <div className="summary-label">Live Version</div>
          <div className="summary-value">{config?.version || "—"}</div>
          <div className="summary-sub">{config?.name || "Current config"}</div>
        </div>
        <div className="ai-playground-summary-card">
          <div className="summary-label">Model</div>
          <div className="summary-value">
            {form.default_model.replace("claude-", "").replace("-4-6", "")}
          </div>
          <div className="summary-sub">sandbox and published default</div>
        </div>
        <div className="ai-playground-summary-card">
          <div className="summary-label">Prompt Size</div>
          <div className="summary-value">
            {promptStats.characters.toLocaleString()}
          </div>
          <div className="summary-sub">{promptStats.words} words</div>
        </div>
        <div className="ai-playground-summary-card">
          <div className="summary-label">Draft</div>
          <div className="summary-value">{draftId ? "Saved" : "Unsaved"}</div>
          <div className="summary-sub">
            {draftId ? `Draft #${draftId}` : "not yet saved"}
          </div>
        </div>
      </div>

      <div className="ai-playground-grid">
        <div className="ai-playground-editor-card">
          <div className="ai-card-header">
            <h3>System Prompt</h3>
            <span className="ai-playground-badge">Draft editor</span>
          </div>
          <div className="ai-config-fields">
            <div className="ai-field">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
            <div className="ai-field">
              <label>Version</label>
              <input
                value={form.version}
                onChange={(e) => handleChange("version", e.target.value)}
                placeholder="Example: 1.1"
              />
            </div>
            <div className="ai-field">
              <label>Model</label>
              <select
                value={form.default_model}
                onChange={(e) => handleChange("default_model", e.target.value)}
              >
                {modelOptions.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
            <div className="ai-field">
              <label>Temperature</label>
              <input
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={form.temperature}
                onChange={(e) => handleChange("temperature", e.target.value)}
              />
            </div>
            <div className="ai-field">
              <label>Max Tokens</label>
              <input
                type="number"
                min="1"
                max="8000"
                value={form.max_tokens}
                onChange={(e) => handleChange("max_tokens", e.target.value)}
              />
            </div>
            <div className="ai-field">
              <label>Cache Hours</label>
              <input
                type="number"
                min="1"
                max="168"
                value={form.cache_expiry_hours}
                onChange={(e) =>
                  handleChange("cache_expiry_hours", e.target.value)
                }
              />
            </div>
          </div>
          <div className="ai-prompt-patch-panel">
            <div className="ai-patch-copy">
              <h4>Focused Update</h4>
              <p>
                Tell the assistant what should change. It will update only the
                relevant section or add a new section when needed.
              </p>
            </div>
            <div className="ai-patch-controls">
              <select
                value={patchMode}
                onChange={(e) => setPatchMode(e.target.value)}
                disabled={isPatching}
              >
                <option value="auto">Auto decide</option>
                <option value="update_section">Update existing section</option>
                <option value="add_section">Add new section</option>
              </select>
              <button
                onClick={generatePromptPatch}
                disabled={isPatching || !patchRequest.trim()}
              >
                {isPatching ? "Generating..." : "Generate Update"}
              </button>
            </div>
            <textarea
              value={patchRequest}
              onChange={(e) => setPatchRequest(e.target.value)}
              placeholder="Example: Make sleep advice more practical for parents of 2-year-olds, but keep medical safety rules unchanged."
              disabled={isPatching}
            />
            {patchSuggestion && (
              <div className="ai-patch-review">
                <div>
                  <span>Changed Section</span>
                  <strong>{patchSuggestion.changed_section || "Best match"}</strong>
                </div>
                <div>
                  <span>Summary</span>
                  <p>{patchSuggestion.summary || "Prompt update generated."}</p>
                </div>
                <div className="ai-patch-review-actions">
                  <button onClick={() => setPatchSuggestion(null)}>
                    Dismiss
                  </button>
                  <button
                    className="apply"
                    onClick={applyPromptPatch}
                    disabled={isApplyingPatch}
                  >
                    {isApplyingPatch ? "Applying..." : "Apply to Draft"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            className={`ai-prompt-editor-shell ${
              isApplyingPatch ? "applying" : ""
            } ${didApplyPatch ? "applied" : ""}`}
          >
            {isApplyingPatch && (
              <div className="ai-prompt-apply-overlay">
                <span className="ai-prompt-apply-spinner" />
                <span>Applying focused update to draft...</span>
              </div>
            )}
            {didApplyPatch && (
              <div className="ai-prompt-applied-chip">
                Showing applied text
                {appliedRange
                  ? ` (${Math.max(
                      1,
                      form.system_prompt.slice(0, appliedRange.start).split("\n")
                        .length
                    )})`
                  : ""}
              </div>
            )}
            <textarea
              ref={promptTextareaRef}
              className="ai-prompt-textarea"
              value={form.system_prompt}
              onChange={(e) => handleChange("system_prompt", e.target.value)}
              spellCheck="false"
              disabled={isApplyingPatch}
            />
          </div>
          <div className="ai-field release-notes-field">
            <label>Release Notes</label>
            <input
              value={form.release_notes}
              onChange={(e) => handleChange("release_notes", e.target.value)}
              placeholder="What changed in this prompt?"
            />
          </div>
        </div>

        <div className="ai-playground-chat-card">
          <div className="ai-card-header">
            <h3>Sandbox Chat</h3>
            <button onClick={clearChat} disabled={isTesting}>
              Clear
            </button>
          </div>
          <div className="ai-playground-chat-messages" ref={messagesRef}>
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`playground-message ${message.role}`}
              >
                <div className="playground-message-bubble">
                  {message.content}
                </div>
                {message.meta && (
                  <div className="playground-message-meta">
                    <span>{message.meta.model}</span>
                    <span>{message.meta.tokens.toLocaleString()} tokens</span>
                    <span>
                      {message.meta.responseTime > 1000
                        ? `${(message.meta.responseTime / 1000).toFixed(1)}s`
                        : `${message.meta.responseTime}ms`}
                    </span>
                  </div>
                )}
              </div>
            ))}
            {isTesting && (
              <div className="playground-message assistant">
                <div className="playground-message-bubble loading">
                  Testing prompt...
                </div>
              </div>
            )}
          </div>
          <div className="ai-playground-composer">
            <textarea
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder="Send a test parent message..."
              disabled={isTesting}
            />
            <button onClick={sendTestMessage} disabled={isTesting || !testInput.trim()}>
              Send Test
            </button>
          </div>
        </div>
      </div>

      <div className="ai-playground-guides">
        <div className="ai-playground-guide-card">
          <h3>How to modify the prompt</h3>
          <p>
            Write behavior rules in plain language. Say what the AI should do,
            when it should do it, and what it should avoid.
          </p>
        </div>
        <div className="ai-playground-guide-card">
          <h3>Best practices</h3>
          <ul>
            <li>Keep tone rules short and direct.</li>
            <li>Test food, sleep, crying, safety, and emotional examples.</li>
            <li>Save drafts while experimenting, then publish only the final version.</li>
          </ul>
        </div>
        <div className="ai-playground-guide-card warning">
          <h3>Do not change lightly</h3>
          <p>
            Medical safety, crisis handling, privacy, and non-judgmental support
            rules should remain clear in every published prompt.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIPlayground;
